import {
  type AroundATimeState,
  Box,
  IconButton,
  styled,
  type TimeRangeSelectionMode,
  Tooltip,
  useBreakpointDown,
  useCopyToClipboard,
} from '@chronosphereio/chrono-ui';
import {
  isTimeRangeInputValueAbsolute,
  TimeFormat,
  type TimeRangeInputValue,
  useFeatureFlags,
} from '@chronosphereio/core';
import { modifierKeyPrefix } from '@chronosphereio/shared';
// this popover is more complex than the one provided by chrono-ui, so we're using mui's popover directly
// eslint-disable-next-line no-restricted-imports
import { Popover } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from 'mdi-material-ui';
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTimeZone } from '@/components/context';
import { NamedTimeOption } from '@/core';
import { useTimeRange } from '@/plugin-system/runtime';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { TimeRangeTextFields } from '../TimeRangeTextFields/TimeRangeTextFields';
import { absoluteTimeRangeDatesHaveCorrectOrder, formatTimeRangeInputValue, parseTimeRange } from '../utils';
import {
  ErrorOptionKey,
  filterTimeRangeOptions,
  getErrorOptionLabels,
  getTimeRangeSelectOptions,
  TimeOption,
} from '../utils/options';
import { useTimeZoneDetails } from '../utils/useTimeZoneDetails';
import { CollapsibleSection } from './CollapsibleSection';
import { CopyTimeRangeMenu } from './CopyTimeRangeMenu';
import { RelativeTimeRangeList } from './RelativeTimeRangeList';
import { TimeRangeSearchField } from './TimeRangeSearchField';
import { TimeZoneSection } from './TimeZoneSection';

export interface TimeRangeSelectPanelProps {
  /**
   * Whether the panel is open
   */
  open: boolean;
  /**
   * The anchor element for positioning the panel
   */
  anchorEl: HTMLElement | null;
  /**
   * Callback to close the panel
   */
  onClose: () => void;
  /** State for the "Around a time" data. */
  aroundATime: AroundATimeState;
  /** Whether the picker is in "From / To" or "Around a time" mode. */
  timeRangeSelectionMode?: TimeRangeSelectionMode;
  /** Callback to update the time range selection mode. */
  setTimeRangeSelectionMode?: (mode: TimeRangeSelectionMode) => void;
  /**
   * Current time zone
   */
  timeZone?: string;
  /**
   * Current time format
   */
  timeFormat: TimeFormat;
  /**
   * The minimum date that can be selected in the calendar.
   */
  minDate?: Date;
  /**
   * The maximum date that can be selected in the calendar.
   */
  maxDate?: Date;
  /**
   * Current input error state
   */
  inputError?: false | ErrorOptionKey;
  /**
   * The user's account time zone
   */
  userTimeZone?: string;
  /**
   * Callback to override the user time zone
   */
  setTimeZone?: (timeZone: string) => void;
  /**
   * Whether time zone override is disabled
   */
  disableTimeZoneOverride?: boolean;
  /**
   * Last loaded message to display within the panel on mobile
   */
  lastLoadedMessage?: string;
  /**
   * Callback to set input error state
   */
  onInputError: (error: false | ErrorOptionKey) => void;
  /**
   * The time range options to display
   */
  timeOptions?: NamedTimeOption | TimeOption[];
  /**
   * When provided, called with the new time range when the user applies.
   * Allows the parent (e.g. TimeRangeSelect) to update controlled state so the combobox reflects the change.
   */
  onChange?: (newTimeRange: TimeRangeInputValue) => void;
  /**
   * When the panel opens with an input error, this value is used to populate
   * the search field instead of the current time range.
   */
  errorInputValue?: string;
}

/**
 * Panel component that displays the time range picker with two columns:
 * - Left side: Date picker and absolute time range picker
 * - Right side: List of relative time range options
 */
export function TimeRangeSelectPanel({
  open,
  anchorEl,
  onClose,
  timeZone,
  timeFormat,
  minDate,
  maxDate,
  inputError,
  userTimeZone,
  setTimeZone,
  disableTimeZoneOverride,
  lastLoadedMessage,
  onInputError,
  timeOptions: initTimeOptionsProp,
  onChange,
  errorInputValue,
  aroundATime,
  timeRangeSelectionMode,
  setTimeRangeSelectionMode,
}: TimeRangeSelectPanelProps) {
  const { panwRebrandStubYear } = useFeatureFlags();
  const errorOptionLabels = getErrorOptionLabels(panwRebrandStubYear);
  const { absoluteTimeRange, timeRange, setTimeRange } = useTimeRange();
  const isMobile = useBreakpointDown('sm', { breakpointSource: 'viewport' });
  const relativeTimeRangeSelected = !isTimeRangeInputValueAbsolute(timeRange);
  const { timeZone: contextTimeZone } = useTimeZone();
  const userTimeFormat = useUserTimeFormat();
  const copy = useCopyToClipboard();

  // Use props if provided, otherwise fall back to context
  const effectiveTimeZone = timeZone ?? contextTimeZone;
  const effectiveTimeFormat = timeFormat ?? userTimeFormat;

  const [calendarExpanded, setCalendarExpanded] = useState(!relativeTimeRangeSelected);
  const [relativeRangesExpanded, setRelativeRangesExpanded] = useState(relativeTimeRangeSelected);
  const [copyMenuAnchorEl, setCopyMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const copyMenuOpen = Boolean(copyMenuAnchorEl);

  // Search field state
  const [searchValue, setSearchValue] = useState('');
  const [userHasEdited, setUserHasEdited] = useState(false);
  const listboxId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-initialize search field with the current formatted value when the panel opens
  const prevOpenRef = useRef(open);
  if (open && !prevOpenRef.current) {
    const formattedValue =
      errorInputValue ??
      formatTimeRangeInputValue({
        timeRange,
        timeFormat: effectiveTimeFormat,
        timeZone: effectiveTimeZone,
      });
    setSearchValue(formattedValue);
    setUserHasEdited(false);
  }
  prevOpenRef.current = open;

  const { timeOptions } = useMemo(() => {
    return getTimeRangeSelectOptions({
      initTimeOptions: initTimeOptionsProp ?? 'default',
      timeFormat,
      timeZone,
    });
  }, [initTimeOptionsProp, timeFormat, timeZone]);

  const filteredOptions = useMemo(() => {
    if (!userHasEdited) return timeOptions;
    return filterTimeRangeOptions(timeOptions, { inputValue: searchValue, includeSyntheticOptions: true });
  }, [timeOptions, searchValue, userHasEdited]);

  useEffect(() => {
    setCalendarExpanded(!relativeTimeRangeSelected);
    setRelativeRangesExpanded(relativeTimeRangeSelected);
  }, [relativeTimeRangeSelected]);

  const handleTimeRangeSelect = (newTimeRange: TimeRangeInputValue) => {
    onChange?.(newTimeRange);
    setTimeRange(newTimeRange);
    onClose();
  };

  const copyFormattedTimeRange = async (range: TimeRangeInputValue) => {
    const formatted = formatTimeRangeInputValue({
      timeRange: range,
      timeFormat: effectiveTimeFormat,
      timeZone: effectiveTimeZone,
    });
    await copy(formatted, 'Copied time range');
  };

  const handleCopyAbsolute = () => copyFormattedTimeRange(absoluteTimeRange);
  const handleCopyRelative = () => copyFormattedTimeRange(timeRange);

  const handleCopyClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (relativeTimeRangeSelected) {
      setCopyMenuAnchorEl(event.currentTarget);
    } else {
      // For absolute time ranges, copy directly
      await handleCopyAbsolute();
    }
  };

  async function handlePaste() {
    const unsupportedError = timeFormat === '24h' ? 'UNSUPPORTED_24H' : 'UNSUPPORTED_12H';

    try {
      const clipboardText = await navigator.clipboard.readText();

      if (!clipboardText?.trim()) {
        // Empty clipboard
        onInputError(unsupportedError);
        return;
      }

      const potentialNewDate = parseTimeRange(clipboardText, effectiveTimeZone);
      if (!potentialNewDate) {
        // The pasted text does not lead to a parsed date
        onInputError(unsupportedError);
      } else if (
        isTimeRangeInputValueAbsolute(potentialNewDate) &&
        !absoluteTimeRangeDatesHaveCorrectOrder(potentialNewDate)
      ) {
        // The pasted text leads to a parsed absolute date where the start/end dates are in the wrong order
        onInputError('INVALID_ORDER');
      } else {
        setTimeRange(potentialNewDate);
        onInputError(false);
        onClose();
      }
    } catch (error) {
      // clipboard API errors
      console.error('Failed to read from clipboard:', error);
      onInputError(unsupportedError);
    }
  }

  // Handle keyboard shortcuts for copy/paste when inputs aren't focused
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const isModifierPressed = event.metaKey || event.ctrlKey;
    const target = event.target as Element;
    const isInputFocused = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

    if (!isModifierPressed || isInputFocused) return;

    if (event.key.toLowerCase() === 'c') {
      event.preventDefault();
      // Copy the current time range value as-is (relative stays relative, absolute stays absolute)
      void copyFormattedTimeRange(timeRange);
    } else if (event.key.toLowerCase() === 'v') {
      event.preventDefault();
      void handlePaste();
    }
  };
  const timeZoneDetails = useTimeZoneDetails({ timeZone });

  const CopyIconButton = (
    <>
      <Tooltip Content={`Copy time range (${modifierKeyPrefix()}C)`}>
        <IconButton onClick={handleCopyClick} aria-label="Copy time range" variant="secondary">
          <ContentCopyIcon />
        </IconButton>
      </Tooltip>
      <CopyTimeRangeMenu
        anchorEl={copyMenuAnchorEl}
        open={copyMenuOpen}
        onClose={() => setCopyMenuAnchorEl(null)}
        timeRange={timeRange}
        absoluteTimeRange={absoluteTimeRange}
        onCopyAbsolute={handleCopyAbsolute}
        onCopyRelative={handleCopyRelative}
        ariaLabel="Copy time range"
        timeZone={effectiveTimeZone}
        timeFormat={effectiveTimeFormat}
        copyingJustTimestamp
      />
    </>
  );

  const TimeZoneSelect = (
    <TimeZoneSection
      userTimeZone={userTimeZone}
      setTimeZone={setTimeZone}
      timeZone={timeZoneDetails.IANA}
      disableTimeZoneOverride={disableTimeZoneOverride}
    />
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <PopoverContentContainer isMobile={isMobile}>
        <TimeRangeSearchField
          searchValue={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setUserHasEdited(true);
            if (inputError) onInputError(false);
          }}
          onSelect={handleTimeRangeSelect}
          timeZone={effectiveTimeZone}
          timeFormat={timeFormat}
          copyButton={CopyIconButton}
          lastLoadedMessage={lastLoadedMessage}
          panwRebrandStubYear={panwRebrandStubYear}
          listboxId={listboxId}
          listRef={listRef}
          inputRef={inputRef}
          hasFilteredOptions={filteredOptions.length > 0}
          errorMessage={inputError ? errorOptionLabels[inputError] : undefined}
        />
        <TimeSelectionSection isMobile={isMobile}>
          <LeftColumn isMobile={isMobile}>
            <CollapsibleSection
              title="Preset ranges"
              expanded={relativeRangesExpanded}
              onToggle={() => {
                setRelativeRangesExpanded(!relativeRangesExpanded);
                setCalendarExpanded(false);
              }}
            >
              <RelativeTimeRangeList
                ref={listRef}
                value={timeRange}
                onChange={handleTimeRangeSelect}
                filteredOptions={filteredOptions}
                listboxId={listboxId}
                onEscape={() => inputRef.current?.focus()}
              />
            </CollapsibleSection>
          </LeftColumn>
          <RightColumn isMobile={isMobile}>
            <CollapsibleSection
              title="Calendar selection"
              expanded={calendarExpanded}
              onToggle={() => {
                setCalendarExpanded(!calendarExpanded);
                setRelativeRangesExpanded(false);
              }}
            >
              <TimeRangeTextFields
                initialTimeRange={timeRange}
                onTimeRangeChange={handleTimeRangeSelect}
                timeZone={effectiveTimeZone}
                minDate={minDate}
                maxDate={maxDate}
                aroundATime={aroundATime}
                timeRangeSelectionMode={timeRangeSelectionMode}
                setTimeRangeSelectionMode={setTimeRangeSelectionMode}
              />
            </CollapsibleSection>
          </RightColumn>
        </TimeSelectionSection>
        {!isMobile && TimeZoneSelect}
      </PopoverContentContainer>
      {isMobile && TimeZoneSelect}
    </Popover>
  );
}

const PopoverContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ theme, isMobile }) => ({
  background: theme.palette.background.popover,
  borderRadius: theme.featureFlags.panwRebrandStubYear ? theme.shape.borderRadiusXs : undefined,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: isMobile ? theme.spacing(48) : theme.spacing(70),
  maxHeight: '70vh',
  ...(isMobile && { width: '90vw' }),
  overflow: 'hidden',
}));

const TimeSelectionSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  flex: 1,
  minHeight: 0,
}));

const LeftColumn = styled(Box, { shouldForwardProp: (prop) => prop !== 'isMobile' })<{ isMobile: boolean }>(
  ({ theme, isMobile }) => ({
    borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
    minWidth: isMobile ? undefined : theme.spacing(22),
    maxWidth: isMobile ? '100%' : theme.spacing(44),
    // limiting the height of the left column so it only extends to the height of the right column
    // we also want half of a preset range to be visible to give users an indication that there
    // are more options available when the max height is reached
    maxHeight: '496px',
    overflowY: 'auto',
  })
);

const RightColumn = styled(Box, { shouldForwardProp: (prop) => prop !== 'isMobile' })<{ isMobile: boolean }>(
  ({ theme, isMobile }) => {
    return {
      borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
      overflowY: 'auto',
    };
  }
);
